import { MOTION_EDITOR_APPS_MENU_VIDEO } from '@cartwhl/ui/organisms/AppsMenu';

import type { AssetLibraryMediaSelection } from './assetLibraryMediaSelection';
import { toAssetLibraryMediaSelection } from './assetLibraryMediaSelection';
import type { AssetLibraryView } from './assetLibraryView';
import {
  type AssetLibraryDisplayItem,
  getAssetCardTitle,
  isCardMoveToTrashVisible,
  shouldShowStackedPreview,
} from './utils';

const DEMO_MEDIA_BASE = 'https://resources.getcartwheel.com/share/demo-videos';

export type AssetLibraryOpenWithAppId = 'motion-editor' | 'comic' | 'mascot';

export type MascotOpenWithAction = {
  id: string;
  label: string;
  slot: 'motion' | 'character' | 'background';
};

export type AssetLibraryOpenWithOption =
  | {
      app: 'motion-editor' | 'comic';
      label: string;
      subtitle: string;
      imageSrc: string;
      onSelect: () => void;
    }
  | {
      app: 'mascot';
      label: string;
      subtitle: string;
      imageSrc: string;
      actions: MascotOpenWithAction[];
      onSelectAction: (action: MascotOpenWithAction) => void;
    };

const OPEN_WITH_APP_META: Record<
  AssetLibraryOpenWithAppId,
  { label: string; subtitle: string; imageSrc: string }
> = {
  'motion-editor': {
    label: 'Performance Editor',
    subtitle: 'Generative editing and reshaping',
    imageSrc: MOTION_EDITOR_APPS_MENU_VIDEO,
  },
  comic: {
    label: 'Performance Capture',
    subtitle: 'AI motion capture',
    imageSrc: `${DEMO_MEDIA_BASE}/app_launcher_comic.webm`,
  },
  mascot: {
    label: 'Mascot',
    subtitle: 'Video Character Replacement',
    imageSrc: `${DEMO_MEDIA_BASE}/app_launcher_mascot.webm`,
  },
};

export const MASCOT_VIDEO_OPEN_WITH_ACTION: MascotOpenWithAction = {
  id: 'mascot-motion',
  label: 'Open as Motion reference',
  slot: 'motion',
};

export const MASCOT_IMAGE_OPEN_WITH_ACTIONS: MascotOpenWithAction[] = [
  {
    id: 'mascot-character',
    label: 'Open as Character reference',
    slot: 'character',
  },
  {
    id: 'mascot-background',
    label: 'Open as Background reference',
    slot: 'background',
  },
];

export function getOpenWithAppMeta(
  app: Extract<AssetLibraryOpenWithAppId, 'comic' | 'mascot'>,
) {
  return OPEN_WITH_APP_META[app];
}

export function getAssetLibraryOpenPreviewHandler(
  asset: AssetLibraryDisplayItem,
  onClick?: () => void,
): (() => void) | undefined {
  if (!onClick || asset.assetType === 'SCENE') {
    return undefined;
  }

  return onClick;
}

export type AssetLibraryOpenWithCallbacks = {
  onOpenMotionEditorFromJob: (input: {
    jobId: string;
    label: string;
    outputCount?: number;
  }) => void;
  onOpenSceneInMotionEditor: (sceneId: string) => void;
  onOpenComic: (selection: AssetLibraryMediaSelection) => void;
  onOpenMascot: (
    slot: MascotOpenWithAction['slot'],
    selection: AssetLibraryMediaSelection,
  ) => void;
};

export type AssetLibraryCardMoreMenuState = {
  openWithOptions: AssetLibraryOpenWithOption[];
  onOpenPreview?: () => void;
  onCopyShareLink?: () => void;
  showMoveToTrash?: boolean;
  showMoveToLibrary?: boolean;
};

export function resolveAssetLibraryCardMoreMenu(
  input: {
    asset: AssetLibraryDisplayItem;
    embedded: boolean;
    libraryView: AssetLibraryView;
    isGroupDetailItem?: boolean;
    onClick?: () => void;
  } & AssetLibraryOpenWithCallbacks,
): AssetLibraryCardMoreMenuState | null {
  if (input.libraryView === 'trash') {
    return {
      openWithOptions: [],
      showMoveToLibrary: true,
    };
  }

  if (input.embedded) {
    return null;
  }

  if (shouldShowStackedPreview(input.asset) && !input.isGroupDetailItem) {
    return {
      openWithOptions: [],
      showMoveToTrash: isCardMoveToTrashVisible(input.libraryView),
    };
  }

  const onOpenPreview = getAssetLibraryOpenPreviewHandler(
    input.asset,
    input.onClick,
  );
  const openWithOptions = buildAssetLibraryOpenWithOptions({
    asset: input.asset,
    onOpenMotionEditorFromJob: input.onOpenMotionEditorFromJob,
    onOpenSceneInMotionEditor: input.onOpenSceneInMotionEditor,
    onOpenComic: input.onOpenComic,
    onOpenMascot: input.onOpenMascot,
  });

  const showMoveToTrash =
    isCardMoveToTrashVisible(input.libraryView) && !input.isGroupDetailItem;

  if (openWithOptions.length === 0 && !onOpenPreview && !showMoveToTrash) {
    return null;
  }

  return { openWithOptions, onOpenPreview, showMoveToTrash };
}

function buildComicVideoOpenWithOptions(
  selection: AssetLibraryMediaSelection,
  input: {
    onOpenComic: (selection: AssetLibraryMediaSelection) => void;
  },
): AssetLibraryOpenWithOption[] {
  const comicMeta = OPEN_WITH_APP_META.comic;

  return [
    {
      app: 'comic',
      label: comicMeta.label,
      subtitle: comicMeta.subtitle,
      imageSrc: comicMeta.imageSrc,
      onSelect: () => input.onOpenComic(selection),
    },
  ];
}

export function buildAssetLibraryOpenWithOptions(
  input: {
    asset: AssetLibraryDisplayItem;
  } & AssetLibraryOpenWithCallbacks,
): AssetLibraryOpenWithOption[] {
  const { asset } = input;

  if (asset.assetType === 'MOGEN_JOB') {
    const meta = OPEN_WITH_APP_META['motion-editor'];
    return [
      {
        app: 'motion-editor',
        label: meta.label,
        subtitle: meta.subtitle,
        imageSrc: meta.imageSrc,
        onSelect: () =>
          input.onOpenMotionEditorFromJob({
            jobId: asset.assetID,
            label: getAssetCardTitle(asset),
            outputCount: asset.outputCount,
          }),
      },
    ];
  }

  if (asset.assetType === 'SCENE') {
    const meta = OPEN_WITH_APP_META['motion-editor'];
    return [
      {
        app: 'motion-editor',
        label: meta.label,
        subtitle: meta.subtitle,
        imageSrc: meta.imageSrc,
        onSelect: () => input.onOpenSceneInMotionEditor(asset.assetID),
      },
    ];
  }

  if (asset.assetType === 'MEDIA_VIDEO') {
    const selection = toAssetLibraryMediaSelection(asset);
    if (!selection) {
      return [];
    }

    return buildComicVideoOpenWithOptions(selection, input);
  }

  return [];
}
